import { Module } from 'vuex';
import {
  action,
  actionCreatorFactory,
  combineAction,
  combineMutation,
  mutation,
} from 'vuex-typescript-fsa/lib';
import { RootState } from '@/store';
import {
  Cv,
  CvResponse,
  CreateCvFromScratchRequest,
  CreateCvFromCopyRequest,
  CreateCvFromTemplateRequest,
  CreateCvFromExampleRequest,
  CvDesignTemplateResponse,
  CvDesignTemplate,
  CvDesignTemplateSampleResponse,
  CvDesignTemplateSample,
  CvSample,
  CvSampleResponse,
} from '@/../gen';
import { CvExample, CvExampleResponse } from '../../../gen';

export const namespace = 'cvs';
const actionCreator = actionCreatorFactory(namespace);
export const GetCvs = actionCreator<null>('GET_CVS');

export const GetCvDesignTemplateSamples = actionCreator<null>(
  'GET_CV_DESIGN_TEMPLATE_SAMPLES'
);
export const GetCvDesignTemplates = actionCreator<null>(
  'GET_CV_DESIGN_TEMPLATES'
);
export const GetCvExamples = actionCreator<null>('GET_CV_EXAMPLES');
export const CreateCvFromScratch = actionCreator<CreateCvFromScratchRequest>(
  'CREATE_CV_FROM_SCRATCH'
);
export const CreateCvFromCopy = actionCreator<CreateCvFromCopyRequest>(
  'CREATE_CV_FROM_COPY'
);
export const CreateCvFromTemplate = actionCreator<CreateCvFromTemplateRequest>(
  'CREATE_CV_FROM_TEMPLATE'
);
export const CreateCvFromExample = actionCreator<CreateCvFromExampleRequest>(
  'CREATE_CV_FROM_EXAMPLE'
);
export const SetCvs = actionCreator<CvResponse[]>('SET_CVS');
export const AddCv = actionCreator<CvResponse>('ADD_CVS');

export const SetDesignTemplateSamples = actionCreator<
  CvDesignTemplateSampleResponse[]
>('SET_CV_DESIGN_TEMPLATE_SAMPLES');
export const SetDesignTemplates = actionCreator<CvDesignTemplateResponse[]>(
  'SET_CV_DESIGN_TEMPLATES'
);
export const SetCvExamples = actionCreator<CvExampleResponse[]>(
  'SET_CV_EXAMPLES'
);
export const ResetState = actionCreator<null>('RESET_STATE');

export interface State {
  cvs: Cv[];
  cvDesignTemplateSamples: CvDesignTemplateSample[];
  cvExamples: CvExample[];
  cvDesignTemplates: CvDesignTemplate[];
}

const initialState = (): State => {
  return {
    cvs: [],
    cvDesignTemplateSamples: [],
    cvExamples: [],
    cvDesignTemplates: [],
  };
};

export const module: Module<State, RootState> = {
  namespaced: true,
  state: initialState,
  actions: combineAction(
    action(GetCvs, async function(context) {
      const { data } = await this.proffApi.listCvs();
      context.commit(SetCvs(data.data));
    }),
    action(GetCvDesignTemplateSamples, async function(context) {
      const { data } = await this.proffApi.listCvDesignTemplateSamples();
      context.commit(SetDesignTemplateSamples(data.data));
    }),
    action(GetCvDesignTemplates, async function(context) {
      const { data } = await this.proffApi.listCvDesignTemplates();
      context.commit(SetDesignTemplates(data.data));
    }),
    action(GetCvExamples, async function(context) {
      const { data } = await this.proffApi.listCvExamples();
      context.commit(SetCvExamples(data.data));
    }),
    action(CreateCvFromScratch, async function(context, action) {
      const { data } = await this.proffApi.createCvFromScratch(action.payload);
      context.commit(AddCv(data.data));
    }),
    action(CreateCvFromCopy, async function(context, action) {
      const { data } = await this.proffApi.createCvFromCopy(action.payload);
      context.commit(AddCv(data.data));
    }),
    action(CreateCvFromTemplate, async function(context, action) {
      const { data } = await this.proffApi.createCvFromTemplate(action.payload);
      context.commit(AddCv(data.data));
    }),
    action(CreateCvFromExample, async function(context, action) {
      const { data } = await this.proffApi.createCvFromExample(action.payload);
      context.commit(AddCv(data.data));
    }),
    action(ResetState, function(context, action) {
      context.commit(ResetState(action.payload));
    })
  ),
  mutations: combineMutation(
    mutation(SetCvs, (state, action) => {
      state.cvs = action.payload.map(item => item.attributes);
    }),
    mutation(AddCv, (state, action) => {
      state.cvs.unshift(action.payload.attributes);
    }),
    mutation(SetDesignTemplateSamples, (state, action) => {
      state.cvDesignTemplateSamples = action.payload.map(
        item => item.attributes
      );
    }),
    mutation(SetDesignTemplates, (state, action) => {
      state.cvDesignTemplates = action.payload.map(item => item.attributes);
    }),
    mutation(SetCvExamples, (state, action) => {
      state.cvExamples = action.payload.map(item => item.attributes);
    }),
    mutation(ResetState, state => {
      Object.assign(state, initialState());
    })
  ),
  getters: {
    cvs: state => state.cvs,
    cvDesignTemplateSamples: state => state.cvDesignTemplateSamples,
    cvDesignTemplates: state => state.cvDesignTemplates,
    cvExamples: state => state.cvExamples,
    latestCv: state => state.cvs[0],
  },
};
